import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Home, PenLine, LayoutGrid, Folder, LogOut, Sun, Moon, Monitor } from 'lucide-react';
import api from '../utils/api';
import useTheme from '../hooks/useTheme';

// 极窄全局栏导航（房子=首页 / 写剧本 / 无限画布 / 空间）—— 全 app 统一同一条栏
const NAV_ITEMS = [
  { to: '/home',    icon: Home,       label: '首页' },
  { to: '/writing', icon: PenLine,    label: '写剧本' },
  { to: '/canvas',  icon: LayoutGrid, label: '无限画布' },
  { to: '/assets',  icon: Folder,     label: '空间' },
];

const THEME_OPTIONS = [
  { value: 'light',  icon: Sun,     label: '浅色' },
  { value: 'dark',   icon: Moon,    label: '深色' },
  { value: 'system', icon: Monitor, label: '跟随系统' },
];

function RailIcon({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      title={label}
      aria-label={label}
      className={({ isActive }) =>
        `flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors
         ${isActive
           ? 'bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7]'
           : 'text-stone-400 hover:bg-[#f3eee5] hover:text-stone-700 dark:text-gray-500 dark:hover:bg-white/[0.05] dark:hover:text-gray-300'
         }`
      }
    >
      <Icon size={19} />
    </NavLink>
  );
}

export default function Layout() {
  const [user, setUser] = useState(null);
  const [themeOpen, setThemeOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUser = useCallback(() => {
    if (localStorage.getItem('token') === 'local-dev-mock-token') {
      setUser({ username: '本地测试', credits: 9999 });
      return;
    }
    api.get('/users/me').then(r => setUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchUser();
    const t = setInterval(fetchUser, 30000);
    return () => clearInterval(t);
  }, [fetchUser]);

  useEffect(() => {
    const handler = () => fetchUser();
    window.addEventListener('credits-updated', handler);
    return () => window.removeEventListener('credits-updated', handler);
  }, [fetchUser]);

  // 用户信息没加载出来时（首帧请求抖动等），切页面时补拉一次，避免侧栏头像卡在 '?'
  useEffect(() => {
    if (!user) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const currentTheme = THEME_OPTIONS.find(t => t.value === theme) || THEME_OPTIONS[2];
  const ThemeIcon = currentTheme.icon;

  return (
    <div className="flex h-screen overflow-hidden bg-[#f4f1e9] text-gray-900 dark:bg-[#0e0e10] dark:text-gray-100">
      {/* ── 极窄全局栏 ── */}
      <aside className="z-40 flex h-screen w-[54px] shrink-0 flex-col items-center border-r border-[#ded6c8] bg-[#fbfaf6] py-3 dark:border-white/[0.06] dark:bg-[#0c0c10]">
        <nav className="flex flex-col items-center gap-1.5">
          {NAV_ITEMS.map(item => <RailIcon key={item.to} {...item} />)}
        </nav>

        <div className="mt-auto flex flex-col items-center gap-1.5">
          {/* 主题切换 */}
          <div className="relative">
            <button
              onClick={() => setThemeOpen(v => !v)}
              title={currentTheme.label}
              aria-label="切换主题"
              className="flex h-9 w-9 items-center justify-center rounded-[10px] text-stone-400 transition-colors hover:bg-[#f3eee5] hover:text-stone-700 dark:text-gray-500 dark:hover:bg-white/[0.05] dark:hover:text-gray-300"
            >
              <ThemeIcon size={18} />
            </button>
            {themeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setThemeOpen(false)} />
                <div className="absolute bottom-0 left-full z-20 ml-2 w-[140px] overflow-hidden rounded-xl border border-[#ded6c8] bg-[#fbfaf6] shadow-lg dark:border-white/[0.08] dark:bg-[#1a1a22]">
                  {THEME_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => { setTheme(opt.value); setThemeOpen(false); }}
                        className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-[13px] leading-none transition-colors
                          ${theme === opt.value
                            ? 'bg-[#eee9de] text-stone-900 dark:bg-white/[0.08] dark:text-gray-100'
                            : 'text-stone-600 hover:bg-[#f3eee5] dark:text-gray-400 dark:hover:bg-white/[0.05]'}`}
                      >
                        <Icon size={14} className="shrink-0" />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* 头像 → 个人中心 */}
          <NavLink
            to="/profile"
            title="个人中心"
            aria-label="个人中心"
            className={({ isActive }) =>
              `flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-medium transition-all
               ${isActive
                 ? 'bg-[#8c7db0] text-white ring-2 ring-[#8c7db0]/40'
                 : 'bg-[#eee9de] text-[#6f6196] hover:bg-[#e5dece] dark:bg-white/[0.08] dark:text-[#b9afe7]'}`
            }
          >
            {user?.username?.[0]?.toUpperCase() || '?'}
          </NavLink>

          {/* 退出 */}
          <button
            onClick={logout}
            title="退出登录"
            aria-label="退出登录"
            className="flex h-9 w-9 items-center justify-center rounded-[10px] text-stone-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-gray-500 dark:hover:bg-red-500/[0.08] dark:hover:text-red-400"
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      {/* ── 内容区 ── */}
      <main className="h-screen min-w-0 flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
